import { useCallback, useEffect, useState } from 'react'
import { ActionButton } from './ActionButton.js'
import { notify, notifyError } from './Toasts.js'
import { api } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Wo bin ich überall noch angemeldet – und wie werde ich es los.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum dieser Bildschirm wichtiger ist als jede Frist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Der Fall, um den es geht, ist der Laptop im Zug und das gestohlene Handy.
 * Dagegen hilft eine Untätigkeitsfrist nur bedingt: sie läuft, und man wartet.
 * Was sofort hilft, ist der Knopf hier – die nächste Anfrage vom verlorenen
 * Gerät läuft ins Leere, Sekunden später.
 *
 * Die Frist bleibt trotzdem richtig. Sie deckt den Fall ab, in dem niemand
 * merkt, dass etwas zu decken wäre.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was hier bewusst NICHT steht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Der Sitzungsschlüssel, auch nicht verkürzt. Diese Liste soll jemandem
 * helfen, sein eigenes Gerät wiederzuerkennen – nicht ein fremdes zu
 * übernehmen.
 *
 * Und kein zweiter Faktor vor dem Beenden. Wer sein Telefon verloren hat, hat
 * womöglich genau den zweiten Faktor verloren; ihn hier zu verlangen hiesse,
 * den Notausgang abzuschliessen. Der Schaden einer unnötigen Abmeldung ist
 * eine neue Anmeldung – der Schaden einer verhinderten ist ein offenes Konto
 * in fremder Hand.
 */

interface Anmeldung {
  id: string
  createdAt: string
  lastSeenAt: string
  userAgent: string
  diese: boolean
}

/**
 * Aus der Browserkennung ein Wort machen, das jemand wiedererkennt.
 *
 * Absichtlich grob. «Safari auf dem iPhone» genügt, um das eigene Gerät zu
 * finden; die vollständige Kennung ist eine Zeile Technik, die niemandem bei
 * der Frage hilft, ob das Ding im Zug liegt.
 */
function geraet(kennung: string): string {
  const k = kennung.toLowerCase()
  const system = k.includes('iphone')
    ? 'iPhone'
    : k.includes('ipad')
      ? 'iPad'
      : k.includes('android')
        ? 'Android-Handy'
        : k.includes('mac os') || k.includes('macintosh')
          ? 'Mac'
          : k.includes('windows')
            ? 'Windows-Rechner'
            : k.includes('linux')
              ? 'Linux-Rechner'
              : 'Unbekanntes Gerät'
  /* Reihenfolge zählt: Chrome und Edge geben sich beide als Safari aus. */
  const browser = k.includes('edg/')
    ? 'Edge'
    : k.includes('chrome/') || k.includes('crios')
      ? 'Chrome'
      : k.includes('firefox') || k.includes('fxios')
        ? 'Firefox'
        : k.includes('safari')
          ? 'Safari'
          : ''
  return browser ? `${browser} auf ${system}` : system
}

/** «vor 3 Stunden» – Zeitpunkte allein beantworten die Frage nicht. */
function seither(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const min = Math.round(ms / 60000)
  if (min < 2) return 'gerade eben'
  if (min < 60) return `vor ${min} Minuten`
  const std = Math.round(min / 60)
  if (std < 24) return `vor ${std} Stunde${std === 1 ? '' : 'n'}`
  const tage = Math.round(std / 24)
  return `vor ${tage} Tag${tage === 1 ? '' : 'en'}`
}

export function Anmeldungen(): JSX.Element {
  const [liste, setListe] = useState<Anmeldung[]>([])
  const [geladen, setGeladen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const r = await api.anmeldungen()
      setListe(r.anmeldungen)
      setFehler(null)
    } catch (e) {
      setFehler(fehlerText(e))
    } finally {
      setGeladen(true)
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  const andere = liste.filter((a) => !a.diese).length

  return (
    <div className="o-card" id="abschnitt-anmeldungen">
      <div className="o-card-head">
        <h3>Angemeldete Geräte</h3>
        <span className="o-soft">{andere === 0 ? 'Nur dieses' : `${andere} weitere`}</span>
      </div>
      <div className="o-pad">
        <p className="o-soft" style={{ marginTop: 0 }}>
          Laptop im Zug liegen gelassen, Handy weg? Hier beendest du alle anderen Anmeldungen. Das
          wirkt sofort — das verlorene Gerät ist beim nächsten Aufruf abgemeldet.
        </p>

        {fehler && (
          <div className="o-band alarm" id="anmeldungen-fehler">
            <span>{fehler}</span>
          </div>
        )}

        {!geladen && <p className="o-soft">Wird geladen …</p>}

        {geladen &&
          liste.map((a) => (
            <div className="o-kasten" key={a.id} style={{ marginTop: 10 }}>
              <div className="o-kasten-kopf">
                <b>
                  {geraet(a.userAgent)}
                  {a.diese && <span className="o-soft"> — dieses Gerät</span>}
                </b>
                <i>zuletzt {seither(a.lastSeenAt)}</i>
              </div>
              <span className="o-soft" style={{ fontSize: 12.5 }}>
                Angemeldet seit {new Date(a.createdAt).toLocaleDateString('de-CH')}
              </span>
            </div>
          ))}

        <div className="o-aktionen" style={{ marginTop: 16 }}>
          <ActionButton
            id="btn-anmeldungen-beenden"
            variant="secondary"
            disabled={andere === 0}
            labels={{
              idle: 'Alle anderen Geräte abmelden',
              busy: 'Wird beendet …',
              success: 'Beendet'
            }}
            onAction={async () => {
              try {
                const r = await api.anmeldungenBeenden(uuidv7())
                notify.ok(
                  r.beendet === 0
                    ? 'Es war keine andere Anmeldung offen.'
                    : `${r.beendet} Anmeldung${r.beendet === 1 ? '' : 'en'} beendet.`
                )
                await laden()
              } catch (e) {
                notifyError(e)
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
